/**
 * Unit asset URL builders.
 *
 * Centralises all floor-plan URL construction so that the filename convention
 * is only defined once. Convention (as of current generator output):
 *
 *   {uuid}_{building}-00-{ref_tier_padded}.{ext}
 *
 * Where:
 * - `building` = first segment of `id_cad` split by '-' (e.g. "0V" from "0V-01-04-M-038")
 * - Second segment is always "00" — the generator currently hard-codes this regardless of
 *   the id_cad value (known upstream quirk, may change in future generator versions)
 * - `ref_tier_padded` = the floor/tier number, zero-padded to 2 digits (e.g. "00", "04", "05")
 * - ext = "svg" for plan mode, "png" for 3D thumb mode
 */

/**
 * Returns the URL for a single floor plan asset.
 *
 * @param {string}  projectDataUrl - Base asset URL (e.g. '/.dev_data/' or 'https://…/project/')
 * @param {{ uuid: string, id_cad: string }} product
 * @param {number}  refTier        - Floor/tier number (from product.tiers[n].ref_tier)
 * @param {'svg'|'3d'} [mode='svg'] - 'svg' → .svg plan, '3d' → .png 3D thumb
 * @returns {string}
 */
export function getFloorPlanUrl(projectDataUrl, product, refTier, mode = 'svg') {
  const base     = projectDataUrl.endsWith('/') ? projectDataUrl : projectDataUrl + '/'
  const building = product.id_cad.split('-')[0]
  const tier     = String(refTier).padStart(2, '0')
  const ext      = mode === '3d' ? 'png' : 'svg' // OLD VERSION FILE AND PATH 
  // const ext      = mode === '3d' ? 'webp' : 'svg'  
  // return `${base}apartments/${product.uuid}/${product.uuid}_${building}-${tier}.${ext}` //
  return `${base}apartments/${product.uuid}/${product.uuid}_${building}-00-${tier}.${ext}` // OLD VERSION FILE AND PATH WITH -00- GATE PLACEHOLDER
}

/**
 * Returns the URL for a single axonometric (render_360) frame.
 *
 * Convention (mirror generator output):
 *   `{dataUrl}apartments/{uuid}/render_360/{uuid}_{building}-00-{ref_tier_padded}_{frame_padded}.png`
 *
 * Frame files are **1-indexed** and **zero-padded to 3 digits**:
 * internal frame index 0 → file `001`, index 7 → file `008`.
 *
 * @param {string}  projectDataUrl
 * @param {{ uuid: string, id_cad: string }} product
 * @param {number}  refTier        - Floor/tier number
 * @param {number}  frameIndex     - 0-based internal frame index
 * @returns {string}
 */
export function getAxonometricFrameUrl(projectDataUrl, product, refTier, frameIndex) {
  const base     = projectDataUrl.endsWith('/') ? projectDataUrl : projectDataUrl + '/'
  const building = product.id_cad.split('-')[0]
  const tier     = String(refTier).padStart(2, '0')
  const frame    = String(frameIndex + 1).padStart(3, '0')
  // return `${base}apartments/${product.uuid}/render_360/${product.uuid}_${building}-${tier}_${frame}.webp`
  return `${base}apartments/${product.uuid}/render_360/${product.uuid}_${building}-00-${tier}_${frame}.png`  // OLD VERSION FILE AND PATH WITH -00- GATE PLACEHOLDER
}

/**
 * Returns the URL for a single compartment-variant SVG fragment.
 *
 * Convention (mirror generator output):
 *   `{dataUrl}apartments/{uuid}/versions/{uuid}_{compartmentNumber_padded}_{version_padded}.svg`
 *
 * Both `compartmentNumber` and `version` are zero-padded to 2 digits.
 * `version` is 1-based — version 0 is the default arrangement embedded in the
 * base floor-plan SVG and is never fetched separately.
 *
 * @param {string}  projectDataUrl
 * @param {{ uuid: string }} product
 * @param {number}  compartmentNumber - 1-based compartment number
 * @param {number}  version           - 1-based variant number (>= 1)
 * @returns {string}
 */
export function getCompartmentVariantUrl(projectDataUrl, product, compartmentNumber, version) {
  const base = projectDataUrl.endsWith('/') ? projectDataUrl : projectDataUrl + '/'
  const n    = String(compartmentNumber).padStart(2, '0')
  const v    = String(version).padStart(2, '0')
  return `${base}apartments/${product.uuid}/versions/${product.uuid}_${n}_${v}.svg`
}

/**
 * Returns the absolute URL for the shared `defs.svg` symbol definitions file.
 *
 * Floor plan SVGs contain `<use href="defs.svg#id">` references with relative paths.
 * Browsers can't resolve those inside inline SVG (`dangerouslySetInnerHTML`), so we
 * rewrite them to absolute URLs before injection — see `rewriteDefsHref()`.
 *
 * @param {string} projectDataUrl
 * @returns {string}
 */
export function getDefsUrl(projectDataUrl) {
  const base = projectDataUrl.endsWith('/') ? projectDataUrl : projectDataUrl + '/'
  return `${base}apartments/defs.svg`
}

/**
 * Rewrites all relative `defs.svg` references in raw SVG text to absolute URLs.
 *
 * Covers both:
 * - `<use href="defs.svg#id">`
 * - `marker-end="url(defs.svg#id)"` (stairway arrows)
 *
 * Global string replace rather than DOM-level manipulation (simpler, sufficient
 * for thumbnail context where we don't further mutate the SVG after injection).
 *
 * @param {string} svgText         - Raw SVG markup
 * @param {string} projectDataUrl  - Base asset URL
 * @returns {string}
 */
export function rewriteDefsHref(svgText, projectDataUrl) {
  const defsUrl = getDefsUrl(projectDataUrl)
  return svgText.replace(/defs\.svg/g, defsUrl)
}
